/**

 * Frontend upload date parser + validation helpers.

 * Backend DateParsingHelper remains source of truth at RunReport time;

 * this module gates bad / mixed / future dates before upload.

 */

import { getDateFormatForCountry } from "./countryDateFormat";

/**

 * Parses upload/run-report dates flexibly.

 * • ISO YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD, YYYY_MM_DD — unambiguous year-first forms.

 * • Named months (Feb, May, 1st Dec, Fri Aug 01 2025) and AM/PM / UTC suffixes stripped when unambiguous.

 * • ISO datetime YYYY-MM-DD[T| ]H:mm[:ss][.sss][Z|±HH:MM] — time stripped (T+offset → UTC date; space → calendar date).

 * • Slash / dash / dot separated — accepted only when the day or month part is > 12.

 * • 2-digit years map to 2000–2099.

 * • Excel Date objects and numeric serials are converted directly.

 *

 * formatHint ("DMY" | "MDY" | null): resolves ambiguous values (both parts ≤ 12).

 */

const sanitizeDateInput = (input) => {
  let s = String(input).trim();
  s = s.replace(/^\uFEFF/, "");
  s = s.replace(/[\u200B-\u200D\u2060]/g, "");
  s = s.replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-");
  s = s.replace(/^[\s"'`“”‘’<>\[\(\{]+|[\s"'`“”‘’<>\]\)\}]+$/g, "");
  return s.trim();
};

const stripTrailingTimeAndTimezone = (input) => {
  let s = sanitizeDateInput(input);
  // UTC/GMT with numeric offset (e.g. "UTC+5:30")
  s = s.replace(/\s*(UTC|GMT|UT)\s*[+-]\d{1,2}(?::?\d{2})?\s*$/i, "");
  s = s.replace(/\s+(UTC|GMT|UT)\s*$/i, "");
  // AM/PM times
  s = s.replace(/\s+\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?\s*(AM|PM)\s*$/i, "");
  // 24h time + optional named timezone (IST, PST, EST, etc.)
  s = s.replace(/\s+\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:\s+[A-Za-z]{2,6})?\s*$/i, "");
  // Standalone named timezone suffix without time (e.g. "2025/01/01 IST")
  s = s.replace(/\s+(?:IST|PST|PDT|EST|EDT|CST|CDT|MST|MDT|HST|AKST|AKDT|AEST|AEDT|BST|CET|CEST|JST|KST|SGT|HKT|NZST|NZDT)\s*$/i, "");
  return s.trim();
};

const preprocessUploadDateString = (input) => {
  let s = stripTrailingTimeAndTimezone(input);
  s = s.replace(/^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*,?\s+/i, "");
  s = s.replace(/\b(\d{1,2})(st|nd|rd|th)\b/gi, "$1");
  return s.trim();
};

const MONTH_NAME_TO_NUM = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const monthNameToNumber = (name) =>
  MONTH_NAME_TO_NUM[
    String(name || "")
      .toLowerCase()
      .slice(0, 3)
  ] || null;

/** Any string with a month name + 4-digit year + day — unambiguous regardless of separators/order. */
const parseFlexibleNamedMonthDate = (input, buildDate) => {
  const monthMatch = input.match(/\b([A-Za-z]{3,})\b/i);
  const yearMatch = input.match(/\b(19|20)\d{2}\b/);
  if (!monthMatch || !yearMatch) return null;
  const month = monthNameToNumber(monthMatch[1]);
  if (!month) return null;
  const year = parseInt(yearMatch[0], 10);
  const dayCandidates = [...input.matchAll(/\b(\d{1,2})\b/g)].map((m) => parseInt(m[1], 10)).filter((n) => n >= 1 && n <= 31);
  if (dayCandidates.length === 0) return null;
  const day = dayCandidates.length === 1 ? dayCandidates[0] : dayCandidates[dayCandidates.length - 1];
  return buildDate(year, month, day);
};

/** Split on any non-digit run — handles comma, tilde, mixed separators when unambiguous. */
const parseFlexibleNumericDate = (input, buildDate, expandYear, resolveMonthDay) => {
  const parts = input.split(/[^0-9]+/).filter((p) => p.length > 0);
  if (parts.length !== 3) return null;
  const nums = parts.map((p) => parseInt(p, 10));
  const yearIdx = nums.findIndex((n) => n >= 1000 && n <= 9999);
  if (yearIdx === 0) {
    return buildDate(nums[0], nums[1], nums[2]);
  }
  if (yearIdx === 2) {
    const year = expandYear(nums[2]);
    const resolved = resolveMonthDay(nums[0], nums[1], year);
    if (!resolved) return null;
    return buildDate(year, resolved.month, resolved.day);
  }
  return null;
};

export const parseUploadDateStrict = (value, formatHint = null) => {
  if (value === null || value === undefined || value === "") return { date: null, error: null };

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const d = new Date(value);

    d.setHours(0, 0, 0, 0);

    return { date: d, error: null };
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const MS_PER_DAY = 86400000;

    const EPOCH_DATE = 25569;

    const epoch = (value - EPOCH_DATE) * MS_PER_DAY;

    const d = new Date(epoch);

    if (!Number.isNaN(d.getTime())) {
      d.setHours(0, 0, 0, 0);

      return { date: d, error: null };
    }

    return { date: null, error: `Invalid numeric date serial ${value}.` };
  }

  const raw = value.toString().trim();

  if (!raw) return { date: null, error: null };

  const text = preprocessUploadDateString(raw);

  const expandYear = (y) => (y < 100 ? 2000 + y : y);

  const buildDate = (year, month, day) => {
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const d = new Date(year, month - 1, day);

    if (Number.isNaN(d.getTime()) || d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;

    d.setHours(0, 0, 0, 0);

    return d;
  };

  const resolveMonthDay = (a, b, year) => {
    if (a > 12 && b > 12) return null;

    if (a > 12) return { day: a, month: b };

    if (b > 12) return { day: b, month: a };

    if (formatHint === "DMY") return { day: a, month: b };

    if (formatHint === "MDY") return { day: b, month: a };

    if (buildDate(year, b, a) !== null) return { day: a, month: b };

    if (buildDate(year, a, b) !== null) return { day: b, month: a };

    return null;
  };

  // ISO datetime with T (+ optional Z/offset).
  // With timezone → UTC calendar date; without timezone → date part only (Excel / local display).
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    const datePartMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})T/);
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw.trim());
    if (datePartMatch && !hasTimezone) {
      const d = buildDate(parseInt(datePartMatch[1], 10), parseInt(datePartMatch[2], 10), parseInt(datePartMatch[3], 10));
      if (d) return { date: d, error: null };
      return { date: null, error: `Invalid date "${raw}".` };
    }
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return { date: buildDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()), error: null };
    }
    return { date: null, error: `Invalid date "${raw}".` };
  }

  // Space-separated datetime (Excel display: "2026-01-15 0:00:00" / "2026-01-15 00:00:00").
  // No timezone in Excel text — use the calendar date from the string.
  const spaceDateTime = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})\s+\d{1,2}:\d{1,2}(?::\d{1,2})?(?:\.\d+)?$/);
  if (spaceDateTime) {
    const d = buildDate(parseInt(spaceDateTime[1], 10), parseInt(spaceDateTime[2], 10), parseInt(spaceDateTime[3], 10));
    if (d) return { date: d, error: null };
    return { date: null, error: `Invalid date "${raw}".` };
  }

  // Trailing time on slash/dash/dot dates — strip and re-parse (fallback if preprocess missed).
  const strippedTime = text.match(/^(.+?)\s+\d{1,2}:\d{1,2}(?::\d{1,2})?(?:\.\d+)?$/);
  if (strippedTime && /[\/.\-_|\\]/.test(strippedTime[1])) {
    return parseUploadDateStrict(strippedTime[1].trim(), formatHint);
  }

  const isoGenericYearFirst = text.match(/^(\d{4})[^A-Za-z0-9]+(\d{1,2})[^A-Za-z0-9]+(\d{1,2})$/);
  if (isoGenericYearFirst) {
    const d = buildDate(parseInt(isoGenericYearFirst[1], 10), parseInt(isoGenericYearFirst[2], 10), parseInt(isoGenericYearFirst[3], 10));
    if (d) return { date: d, error: null };
    return { date: null, error: `Invalid date "${raw}".` };
  }

  const isoMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const d = buildDate(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10), parseInt(isoMatch[3], 10));
    if (d) return { date: d, error: null };
    return { date: null, error: `Invalid date "${raw}".` };
  }

  const isoSlashMatch = text.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (isoSlashMatch) {
    const d = buildDate(parseInt(isoSlashMatch[1], 10), parseInt(isoSlashMatch[2], 10), parseInt(isoSlashMatch[3], 10));
    if (d) return { date: d, error: null };
    return { date: null, error: `Invalid date "${raw}".` };
  }

  const compactIsoMatch = text.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compactIsoMatch) {
    const d = buildDate(parseInt(compactIsoMatch[1], 10), parseInt(compactIsoMatch[2], 10), parseInt(compactIsoMatch[3], 10));
    if (d) return { date: d, error: null };
    return { date: null, error: `Invalid date "${raw}".` };
  }

  const isoDotYearFirst = text.match(/^(\d{4})\.(\d{1,2})\.(\d{1,2})$/);
  if (isoDotYearFirst) {
    const d = buildDate(parseInt(isoDotYearFirst[1], 10), parseInt(isoDotYearFirst[2], 10), parseInt(isoDotYearFirst[3], 10));
    if (d) return { date: d, error: null };
    return { date: null, error: `Invalid date "${raw}".` };
  }

  const isoUnderscoreMatch = text.match(/^(\d{4})_(\d{1,2})_(\d{1,2})$/);
  if (isoUnderscoreMatch) {
    const d = buildDate(parseInt(isoUnderscoreMatch[1], 10), parseInt(isoUnderscoreMatch[2], 10), parseInt(isoUnderscoreMatch[3], 10));
    if (d) return { date: d, error: null };
    return { date: null, error: `Invalid date "${raw}".` };
  }

  const yearMonthNameDay = text.match(/^(\d{4})[-./_]([A-Za-z]{3,})[-./_](\d{1,2})$/i);
  if (yearMonthNameDay) {
    const month = monthNameToNumber(yearMonthNameDay[2]);
    const d = month ? buildDate(parseInt(yearMonthNameDay[1], 10), month, parseInt(yearMonthNameDay[3], 10)) : null;
    if (d) return { date: d, error: null };
    return { date: null, error: `Invalid date "${raw}".` };
  }

  const slashMatch = text.match(/^(\d{1,2})[^A-Za-z0-9]+(\d{1,2})[^A-Za-z0-9]+(\d{2}|\d{4})$/);

  if (slashMatch) {
    const a = parseInt(slashMatch[1], 10);

    const b = parseInt(slashMatch[2], 10);

    const year = expandYear(parseInt(slashMatch[3], 10));

    const resolved = resolveMonthDay(a, b, year);

    if (!resolved) return { date: null, error: `Invalid date "${raw}".` };

    const d = buildDate(year, resolved.month, resolved.day);

    if (d) return { date: d, error: null };

    return { date: null, error: `Invalid date "${raw}".` };
  }

  const dashMatch = text.match(/^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})$/);

  if (dashMatch) {
    const a = parseInt(dashMatch[1], 10);

    const b = parseInt(dashMatch[2], 10);

    const year = expandYear(parseInt(dashMatch[3], 10));

    const resolved = resolveMonthDay(a, b, year);

    if (!resolved) return { date: null, error: `Invalid date "${raw}".` };

    const d = buildDate(year, resolved.month, resolved.day);

    if (d) return { date: d, error: null };

    return { date: null, error: `Invalid date "${raw}".` };
  }

  const dotMatch = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/);

  if (dotMatch) {
    const a = parseInt(dotMatch[1], 10);

    const b = parseInt(dotMatch[2], 10);

    const year = expandYear(parseInt(dotMatch[3], 10));

    const resolved = resolveMonthDay(a, b, year);

    if (!resolved) return { date: null, error: `Invalid date "${raw}".` };

    const d = buildDate(year, resolved.month, resolved.day);

    if (d) return { date: d, error: null };

    return { date: null, error: `Invalid date "${raw}".` };
  }

  const backslashMatch = text.match(/^(\d{1,2})\\(\d{1,2})\\(\d{2}|\d{4})$/);
  if (backslashMatch) {
    const a = parseInt(backslashMatch[1], 10);
    const b = parseInt(backslashMatch[2], 10);
    const year = expandYear(parseInt(backslashMatch[3], 10));
    const resolved = resolveMonthDay(a, b, year);
    if (!resolved) return { date: null, error: `Invalid date "${raw}".` };
    const d = buildDate(year, resolved.month, resolved.day);
    if (d) return { date: d, error: null };
    return { date: null, error: `Invalid date "${raw}".` };
  }

  const monthNum = monthNameToNumber;

  let nm = text.match(/^(\d{1,2})[ \-./]([A-Za-z]{3,}),?[ \-./\s]+(\d{2}|\d{4})$/i);
  if (nm) {
    const day = parseInt(nm[1], 10);
    const month = monthNum(nm[2]);
    const year = expandYear(parseInt(nm[3], 10));
    if (month) {
      const d = buildDate(year, month, day);
      if (d) return { date: d, error: null };
    }
    return { date: null, error: `Invalid date "${raw}".` };
  }

  nm = text.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{2}|\d{4})$/i);
  if (nm) {
    const day = parseInt(nm[1], 10);
    const month = monthNum(nm[2]);
    const year = expandYear(parseInt(nm[3], 10));
    if (month) {
      const d = buildDate(year, month, day);
      if (d) return { date: d, error: null };
    }
    return { date: null, error: `Invalid date "${raw}".` };
  }

  nm = text.match(/^([A-Za-z]{3,})\s+(\d{1,2})\s+(\d{2}|\d{4})$/i);
  if (nm) {
    const month = monthNum(nm[1]);
    const day = parseInt(nm[2], 10);
    const year = expandYear(parseInt(nm[3], 10));
    if (month) {
      const d = buildDate(year, month, day);
      if (d) return { date: d, error: null };
    }
    return { date: null, error: `Invalid date "${raw}".` };
  }

  nm = text.match(/^([A-Za-z]{3,})[ \-./](\d{1,2}),?[ \-./](\d{2}|\d{4})$/i);

  if (nm) {
    const month = monthNum(nm[1]);

    const day = parseInt(nm[2], 10);

    const year = expandYear(parseInt(nm[3], 10));

    if (month) {
      const d = buildDate(year, month, day);

      if (d) return { date: d, error: null };
    }

    return { date: null, error: `Invalid date "${raw}".` };
  }

  nm = text.match(/^([A-Za-z]{3,})[ \-./](\d{4})$/i);

  if (nm) {
    const month = monthNum(nm[1]);

    const year = parseInt(nm[2], 10);

    if (month) {
      const d = buildDate(year, month, 1);

      if (d) return { date: d, error: null };
    }

    return { date: null, error: `Invalid date "${raw}".` };
  }

  nm = text.match(/^(\d{4})[-/_](\d{1,2})$/);

  if (nm) {
    const year = parseInt(nm[1], 10);

    const month = parseInt(nm[2], 10);

    const d = buildDate(year, month, 1);

    if (d) return { date: d, error: null };

    return { date: null, error: `Invalid date "${raw}".` };
  }

  const namedFlex = parseFlexibleNamedMonthDate(text, buildDate);
  if (namedFlex) return { date: namedFlex, error: null };

  const numericFlex = parseFlexibleNumericDate(text, buildDate, expandYear, resolveMonthDay);
  if (numericFlex) return { date: numericFlex, error: null };

  return { date: null, error: `The value "${raw}" is not a recognised date.` };
};

/** Local calendar date → YYYY-MM-DD. */

export const toIsoDateString = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Excel serial → YYYY-MM-DD (UTC date of the serial epoch conversion). */

export const excelSerialToIsoDateString = (serial) => {
  const epoch = Math.round((serial - 25569) * 86400 * 1000);

  const d = new Date(epoch);

  if (Number.isNaN(d.getTime())) return String(serial);

  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
};

/** True when a column header looks like a date field. */

export const isDateColumnHeader = (k) => /date|period|month|from|\bto\b|invoice|year|billing/i.test(String(k || ""));

/**

 * Normalises a date string to ISO YYYY-MM-DD, or returns null if not parseable.

 * Used for Excel text date columns (e.g. newallconfig.js).

 */

export const normalizeTextDate = (str) => {
  if (!str || typeof str !== "string") return null;

  const { date } = parseUploadDateStrict(str.trim(), null);

  return date ? toIsoDateString(date) : null;
};

/**

 * Extracts start/end (or single invoice) dates from a row.

 * Returns { start, end, parseErrors, isSingleDate?, skipPeriodValidation? }.
 *
 * Period Start/End rules apply only when explicit Start/End or From/To date columns exist.
 * Optional HR dates (e.g. Date of joining / leaving) skip FE period validation.

 */

/** HR / wellbeing dates — no future-date or period validation on the upload path. */
const isHrWellbeingDateKey = (keyLower) => keyLower.includes("joining") || keyLower.includes("leaving");

/**
 * Single-date metric columns (Business Travel, PGS, upstream/downstream transport, etc.)
 * that should be parsed and checked for future dates but are not Start/End periods.
 */
export const isStandaloneMetricDateKey = (key) => {
  const kl = String(key || "").toLowerCase();
  if (!kl.includes("date") || isHrWellbeingDateKey(kl)) return false;
  if (/travel\s*date/.test(kl)) return true;
  if (/return\s*date/.test(kl)) return true;
  if (kl.includes("invoice") || kl.includes("transaction")) return true;
  if (kl.includes("transport") && kl.includes("date")) return true;
  return false;
};

const collectStandaloneMetricDates = (record, keys, parseErrors) => {
  const standaloneKeys = keys.filter((k) => isStandaloneMetricDateKey(k));
  if (standaloneKeys.length === 0) return null;

  const standaloneDates = standaloneKeys.map((key) => {
    const raw = record[key];
    const parsed = parseUploadDateStrict(raw);
    if (parsed.error) parseErrors.push(`${key}: ${parsed.error}`);
    return { label: key, date: parsed.date ?? null };
  });

  const firstDate = standaloneDates.find((d) => d.date)?.date ?? standaloneDates[0]?.date ?? null;

  return {
    start: firstDate,
    end: firstDate,
    parseErrors,
    skipPeriodValidation: true,
    standaloneDates,
    isSingleDate: standaloneDates.length === 1,
  };
};

export const getDateRangeFromRecord = (record) => {
  const parseErrors = [];
  if (!record || typeof record !== "object") return { start: null, end: null, parseErrors };

  const keys = Object.keys(record);
  const startKey = keys.find((k) => {
    const kl = k.toLowerCase();
    const hasFROM = /(?:^|[\s_-])from/.test(kl);
    return (kl.includes("start") || hasFROM) && kl.includes("date");
  });
  const endKey = keys.find((k) => {
    const kl = k.toLowerCase();
    const hasTO = /(?:^|[\s_-])to(?:$|[\s_-]|date)/.test(kl);
    return (kl.includes("end") || hasTO) && kl.includes("date");
  });

  let startRaw = startKey ? record[startKey] : null;
  let endRaw = endKey ? record[endKey] : null;

  // No explicit Start/End (or From/To) period columns.
  if (!startKey && !endKey) {
    const standalone = collectStandaloneMetricDates(record, keys, parseErrors);
    if (standalone) return standalone;

    // Employee Wellbeing (joining/leaving) and other non-period date columns:
    // do not require Start/End or block the upload on FE period rules.
    return { start: null, end: null, parseErrors, skipPeriodValidation: true };
  }

  // Explicit period keys only — do not invent a pair from unrelated date columns.
  const startParsed = parseUploadDateStrict(startRaw);
  if (startParsed.error) parseErrors.push(`Date: ${startParsed.error}`);

  const endParsed = parseUploadDateStrict(endRaw);
  if (endParsed.error) parseErrors.push(`End / To date: ${endParsed.error}`);

  const start = startParsed.date;
  const end = endParsed.date;

  if (!start || !end) {
    if (!startParsed.error && !startRaw) parseErrors.push("Start / From date is required.");
    if (!endParsed.error && !endRaw) parseErrors.push("End / To date is required.");
    return { start: null, end: null, parseErrors };
  }

  return { start, end, parseErrors };
};

const startOfDay = (date) => {
  const d = new Date(date);

  d.setHours(0, 0, 0, 0);

  return d;
};

const formatDateKey = (date) => {
  const d = startOfDay(date);

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Human-readable date for overlap / overwrite messages (e.g. "1 Jan 2026"). */

export const formatDateForConflictMessage = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/** Inclusive calendar-day overlap: [startA, endA] intersects [startB, endB]. */

export const doDateRangesOverlap = (startA, endA, startB, endB) => {
  const a0 = startOfDay(startA);

  const a1 = startOfDay(endA);

  const b0 = startOfDay(startB);

  const b1 = startOfDay(endB);

  return a0 <= b1 && b0 <= a1;
};

export const isSameDateRange = (startA, endA, startB, endB) => formatDateKey(startA) === formatDateKey(startB) && formatDateKey(endA) === formatDateKey(endB);

/**

 * Validates uploaded row periods against connector data frequency (monthly / yearly).

 * Runs before overlap checks when reportFrequency is configured.

 */

export const validateUploadDataFrequency = (uploadedRows, reportFrequency) => {
  const errors = [];

  const freq = String(reportFrequency || "")
    .trim()
    .toLowerCase();

  if (!freq || freq === "none") return { errors };

  (uploadedRows || []).forEach((entry) => {
    const range = getDateRangeFromRecord(entry.data);

    if (range.skipPeriodValidation || range.isSingleDate || !range.start || !range.end) return;

    const start = range.start;

    const end = range.end;

    const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();

    const sameYear = start.getFullYear() === end.getFullYear();

    const daySpan = Math.round((startOfDay(end) - startOfDay(start)) / 86400000);

    if (freq.includes("month")) {
      if (!sameMonth && daySpan > 62) {
        errors.push({
          row: entry.rowNumber,

          error: `Date range does not match the connector's monthly data frequency (${reportFrequency}).`,
        });
      }
    } else if (freq.includes("annual") || freq.includes("year")) {
      if (!sameYear || daySpan < 300) {
        errors.push({
          row: entry.rowNumber,

          error: `Date range does not match the connector's yearly data frequency (${reportFrequency}).`,
        });
      }
    }
  });

  return { errors };
};

/**

 * Validates uploaded rows: parse errors, missing pairs, start≥end, future-date rejection,

 * and optional within-file period overlaps.

 *

 * @param {Array<{ rowNumber: number, data: object }>} uploadedRows

 * @param {{ checkPeriodOverlap?: boolean, getTypeKey?: (record: object) => string, typeLabel?: string }} [options]

 *   checkPeriodOverlap — default true for back-compat; set false for format-only metrics

 *   getTypeKey — when set, overlaps only apply within the same type (fuel / refrigerant)

 * @returns {{ errors: Array<{row, error}>, warnings: Array<{row, warning}> }}

 */

export const validateUploadDateRows = (uploadedRows, options = {}) => {
  const { checkPeriodOverlap = true, getTypeKey = null, typeLabel = null } = options;

  const errors = [];

  const warnings = [];

  const today = new Date();

  today.setHours(0, 0, 0, 0);

  const checkFutureDate = (date, label, rowNumber) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;

    const d = new Date(date);

    d.setHours(0, 0, 0, 0);

    if (d > today) {
      errors.push({
        row: rowNumber,

        error: `${label} '${toIsoDateString(d)}' is in the future. Future dates are not allowed.`,
      });
    }
  };

  (uploadedRows || []).forEach((entry) => {
    const { start, end, parseErrors, isSingleDate, skipPeriodValidation, standaloneDates } = getDateRangeFromRecord(entry.data);

    parseErrors.forEach((msg) => errors.push({ row: entry.rowNumber, error: msg }));

    if (standaloneDates?.length) {
      standaloneDates.forEach(({ label, date }) => {
        if (date) checkFutureDate(date, label, entry.rowNumber);
      });
      return;
    }

    if (skipPeriodValidation) return;

    if (isSingleDate) {
      if (start) checkFutureDate(start, "Date", entry.rowNumber);

      return;
    }

    if (!start || !end) return;

    if (start >= end) {
      errors.push({
        row: entry.rowNumber,

        error: "Start Date must be before End Date (same date is not allowed).",
      });
    }

    checkFutureDate(start, "Start Date", entry.rowNumber);

    checkFutureDate(end, "End Date", entry.rowNumber);
  });

  if (checkPeriodOverlap) {
    const seenRangesByKey = new Map();

    for (let i = 0; i < (uploadedRows || []).length; i += 1) {
      const current = uploadedRows[i];

      const currentRange = getDateRangeFromRecord(current.data);

      if (currentRange.skipPeriodValidation || currentRange.isSingleDate) continue;

      if (!currentRange.start || !currentRange.end) continue;

      const typeKey = typeof getTypeKey === "function" ? String(getTypeKey(current.data) || "") : "";

      const groupKey = typeKey || "__period__";

      if (!seenRangesByKey.has(groupKey)) {
        seenRangesByKey.set(groupKey, []);
      }

      const seenRanges = seenRangesByKey.get(groupKey);

      const conflict = seenRanges.find((prev) => doDateRangesOverlap(currentRange.start, currentRange.end, prev.start, prev.end));

      if (conflict) {
        const typePart = typeLabel && typeKey ? ` (${typeLabel}: ${typeKey})` : "";

        errors.push({
          row: current.rowNumber,

          error: `Date range conflict between row ${conflict.rowNumber} (${formatDateForConflictMessage(conflict.start)} – ${formatDateForConflictMessage(conflict.end)}) ` + `and row ${current.rowNumber} (${formatDateForConflictMessage(currentRange.start)} – ${formatDateForConflictMessage(currentRange.end)})${typePart}.`,
        });

        continue;
      }

      seenRanges.push({
        rowNumber: current.rowNumber,

        start: currentRange.start,

        end: currentRange.end,
      });
    }
  }

  return { errors, warnings };
};

/** Contextual summary for Swal date-validation dialogs. */

export const buildDateValidationSummary = (errors) => {
  const texts = (errors || []).map((e) => (e.error || "").toLowerCase());

  const kinds = [];

  if (texts.some((t) => t.includes("required") || t.includes("missing"))) kinds.push("missing");

  if (texts.some((t) => t.includes("ambiguous"))) kinds.push("ambiguous date format");

  if (texts.some((t) => t.includes("must be before") || t.includes("later than") || t.includes("same date"))) {
    kinds.push("out of order (start after end)");
  }

  if (texts.some((t) => t.includes("in the future"))) kinds.push("future dates");

  if (texts.some((t) => t.includes("duplicate time period") || t.includes("date range conflict"))) {
    kinds.push("overlapping / duplicate periods");
  }

  if (texts.some((t) => t.includes("data frequency"))) kinds.push("data frequency mismatch");

  if (texts.some((t) => t.includes("invalid") || t.includes("not a recognised") || t.includes("not a recognized") || t.includes("could not parse") || t.includes("does not exist"))) {
    kinds.push("invalid format");
  }

  if (kinds.length === 0) {
    return "Please review the date errors in the rows below.";
  }

  if (kinds.length === 1) {
    const only = kinds[0];

    if (only === "missing") return "Please review the rows below. One or more start/end dates are missing.";

    if (only === "out of order (start after end)") {
      return "Please review the rows below. Start Date must be before End Date.";
    }

    if (only === "future dates") {
      return "Please review the rows below. Future dates are not allowed.";
    }

    if (only === "overlapping / duplicate periods") {
      return "Please review the rows below. Some date ranges overlap within the uploaded file.";
    }

    if (only === "data frequency mismatch") {
      return "Please review the rows below. Some date ranges do not match the connector data frequency.";
    }

    if (only === "invalid format") {
      return "Please review the rows below. Some dates could not be parsed.";
    }
  }

  return `Please review the rows below. Issues found: ${kinds.join(", ")}.`;
};

/**

 * Infers DMY vs MDY from a column of values.

 * entries: array of { value, rowNumber }.

 */

const SLASHY_DATE_RE = /^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2}|\d{4})$/;

/** Returns "DMY" | "MDY" | "ambiguous" | null for slash/dot/dash numeric dates. */
export const detectSlashDateEvidence = (value) => {
  if (value === null || value === undefined || value instanceof Date || typeof value === "number") return null;

  const raw = String(value).trim();
  const m = raw.match(SLASHY_DATE_RE);
  if (!m) return null;

  const a = parseInt(m[1], 10);
  const b = parseInt(m[2], 10);
  if (a > 12 && b > 12) return null;
  if (a > 12) return "DMY";
  if (b > 12) return "MDY";
  return "ambiguous";
};

export const inferDateColumnFormat = (entries) => {
  const dmyRows = [];
  const mdyRows = [];
  const ambiguousSamples = [];
  let sawSlashy = false;

  (entries || []).forEach(({ value, rowNumber }) => {
    const evidence = detectSlashDateEvidence(value);
    if (!evidence) return;

    sawSlashy = true;
    if (evidence === "DMY") dmyRows.push(rowNumber);
    else if (evidence === "MDY") mdyRows.push(rowNumber);
    else if (ambiguousSamples.length < 3) ambiguousSamples.push(String(value).trim());
  });

  const hasDmyEvidence = dmyRows.length > 0;
  const hasMdyEvidence = mdyRows.length > 0;
  const needsCountryForAmbiguous = (hasDmyEvidence && hasMdyEvidence) || (sawSlashy && !hasDmyEvidence && !hasMdyEvidence);

  if (hasDmyEvidence && !hasMdyEvidence) {
    return { format: "DMY", hasDmyEvidence, hasMdyEvidence, needsCountryForAmbiguous: false, allAmbiguous: false, samples: ambiguousSamples };
  }

  if (hasMdyEvidence && !hasDmyEvidence) {
    return { format: "MDY", hasDmyEvidence, hasMdyEvidence, needsCountryForAmbiguous: false, allAmbiguous: false, samples: ambiguousSamples };
  }

  if (needsCountryForAmbiguous) {
    return {
      format: null,
      hasDmyEvidence,
      hasMdyEvidence,
      needsCountryForAmbiguous: true,
      allAmbiguous: sawSlashy && !hasDmyEvidence && !hasMdyEvidence,
      samples: ambiguousSamples,
    };
  }

  return { format: null, hasDmyEvidence: false, hasMdyEvidence: false, needsCountryForAmbiguous: false, allAmbiguous: false, samples: [] };
};

const ambiguousDateErrorMessage = (columnName, value, inferred) => {
  const reason = inferred.hasDmyEvidence && inferred.hasMdyEvidence ? "the column contains both DD/MM/YYYY and MM/DD/YYYY dates" : "all dates in the column are ambiguous (day and month are both ≤ 12)";

  return `${columnName}: ambiguous date '${value}' cannot be resolved because ${reason}. ` + "Register a country on the site location (used for MM/DD vs DD/MM), or use unambiguous formats (e.g. 31/01/2024 or 2024-01-31).";
};

/** Resolve one cell using per-column inference; unambiguous slash dates keep their own format. */
export const parseColumnDateValue = (value, inferred, countryFormat) => {
  const evidence = detectSlashDateEvidence(value);

  if (evidence === "DMY" || evidence === "MDY") {
    return parseUploadDateStrict(value, evidence);
  }

  if (evidence === "ambiguous") {
    let hint = null;
    if (inferred.format) hint = inferred.format;
    else if (inferred.needsCountryForAmbiguous) hint = countryFormat;

    if (!hint) {
      return { date: null, error: "ambiguous" };
    }

    return parseUploadDateStrict(value, hint);
  }

  return parseUploadDateStrict(value, null);
};

/**
 * Normalises single-date columns (Invoice Date, Travel Date, etc.) per column.
 * Ambiguous slash dates use column-wide DMY/MDY when only one format is present;
 * otherwise site country is required. Returns validation errors (does not throw).
 */
export const normalizeSingleDateColumnsInRows = (convertedData, options = {}) => {
  const errors = [];
  if (!convertedData || convertedData.length === 0) return errors;

  const countryFormat = getDateFormatForCountry(options.country);
  const excludeKeys = new Set(options.excludeKeys || []);
  const dateKeys = Object.keys(convertedData[0] || {}).filter((k) => k !== "__rowNumber" && k.toLowerCase().includes("date") && !excludeKeys.has(k));

  dateKeys.forEach((columnKey) => {
    const entries = [];
    convertedData.forEach((r) => {
      const v = r[columnKey];
      if (v !== null && v !== undefined && v !== "") {
        entries.push({ value: v, rowNumber: r.__rowNumber });
      }
    });

    const inferred = inferDateColumnFormat(entries);
    const hasAmbiguous = entries.some((e) => detectSlashDateEvidence(e.value) === "ambiguous");
    if (hasAmbiguous && inferred.needsCountryForAmbiguous && !countryFormat) {
      errors.push({
        row: entries[0]?.rowNumber ?? 2,
        error: ambiguousDateErrorMessage(columnKey, inferred.samples[0] || "01/02/2024", inferred),
      });
      return;
    }

    convertedData.forEach((r) => {
      const v = r[columnKey];
      if (v === null || v === undefined || v === "" || v instanceof Date) return;
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(v).trim())) return;

      const parsed = parseColumnDateValue(v, inferred, countryFormat);
      if (parsed.error === "ambiguous") {
        errors.push({ row: r.__rowNumber, error: ambiguousDateErrorMessage(columnKey, String(v).trim(), inferred) });
        return;
      }
      if (parsed.date) r[columnKey] = toIsoDateString(parsed.date);
    });
  });

  return errors;
};

/** Start/End pair normalisation + single-date columns. Returns date-normalisation errors. */
export const normalizeUploadDatesInRows = (convertedData, options = {}) => {
  normalizeStartEndPairsInRows(convertedData);

  const dateKeys = Object.keys(convertedData[0] || {}).filter((k) => k !== "__rowNumber" && k.toLowerCase().includes("date"));
  const startKey = dateKeys.find((k) => {
    const kl = k.toLowerCase();
    return (kl.includes("start") || /(?:^|[\s_-])from/.test(kl)) && kl.includes("date");
  });
  const endKey = dateKeys.find((k) => {
    const kl = k.toLowerCase();
    return (kl.includes("end") || /(?:^|[\s_-])to(?:[\s_-]|date|$)/.test(kl)) && kl.includes("date");
  });
  const excludeKeys = startKey && endKey ? [startKey, endKey] : [];

  return normalizeSingleDateColumnsInRows(convertedData, { ...options, excludeKeys });
};

const isAmbiguousSlashDate = (v) => {
  const raw = String(v ?? "").trim();

  if (!/^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}$/.test(raw)) return false;

  const m = raw.match(/^(\d{1,2})[/.-](\d{1,2})/);

  return m && parseInt(m[1], 10) <= 12 && parseInt(m[2], 10) <= 12;
};

const resolveWithPartner = (v, partnerV) => {
  if (v === null || v === undefined || v === "" || v instanceof Date) return null;

  if (partnerV !== null && partnerV !== undefined && partnerV !== "") {
    for (const partnerFmt of [null, "DMY", "MDY"]) {
      const partnerParsed = parseUploadDateStrict(partnerV, partnerFmt);

      if (!partnerParsed.date) continue;

      if (isAmbiguousSlashDate(partnerV) && partnerFmt == null) continue;

      const tMonth = partnerParsed.date.getMonth();

      const tYear = partnerParsed.date.getFullYear();

      for (const fmt of ["DMY", "MDY"]) {
        const attempt = parseUploadDateStrict(v, fmt);

        if (attempt.date && attempt.date.getMonth() === tMonth && attempt.date.getFullYear() === tYear) {
          return attempt.date;
        }
      }

      break;
    }
  }

  const direct = parseUploadDateStrict(v, null);

  return direct.date || null;
};

/**

 * Normalises Start/End date pairs on converted rows to unambiguous ISO.

 * Mutates rows in place. Fixes mixed Excel serial (locale) + European text pairs.

 *

 * @param {object[]} convertedData rows (may include __rowNumber)

 */

export const normalizeStartEndPairsInRows = (convertedData) => {
  if (!convertedData || convertedData.length === 0) return convertedData;

  const dateKeys = Object.keys(convertedData[0] || {}).filter((k) => k !== "__rowNumber" && k.toLowerCase().includes("date"));
  const startKey = dateKeys.find((k) => {
    const kl = k.toLowerCase();
    return (kl.includes("start") || /(?:^|[\s_-])from/.test(kl)) && kl.includes("date");
  });
  const endKey = dateKeys.find((k) => {
    const kl = k.toLowerCase();
    return (kl.includes("end") || /(?:^|[\s_-])to(?:[\s_-]|date|$)/.test(kl)) && kl.includes("date");
  });

  if (startKey && endKey) {
    const dateEntries = [];
    convertedData.forEach((r) => {
      if (r[startKey] != null && r[startKey] !== "") {
        dateEntries.push({ value: r[startKey], rowNumber: r.__rowNumber });
      }
      if (r[endKey] != null && r[endKey] !== "") {
        dateEntries.push({ value: r[endKey], rowNumber: r.__rowNumber });
      }
    });
    const fileFormatHint = inferDateColumnFormat(dateEntries).format || null;

    convertedData.forEach((r) => {
      const sv = r[startKey];
      const ev = r[endKey];
      if ((sv === null || sv === undefined || sv === "") && (ev === null || ev === undefined || ev === "")) {
        return;
      }
      const endSlash = String(ev ?? "")
        .trim()
        .match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
      const startSlash = String(sv ?? "")
        .trim()
        .match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
      let sDate = null;
      let eDate = null;
      if (endSlash && parseInt(endSlash[1], 10) > 12) {
        eDate = parseUploadDateStrict(ev, "DMY").date;
        sDate = resolveWithPartner(sv, ev) || parseUploadDateStrict(sv, "DMY").date;
      } else if (startSlash && parseInt(startSlash[1], 10) > 12) {
        sDate = parseUploadDateStrict(sv, "DMY").date;
        eDate = resolveWithPartner(ev, sv) || parseUploadDateStrict(ev, "DMY").date;
      } else if (endSlash && parseInt(endSlash[2], 10) > 12) {
        eDate = parseUploadDateStrict(ev, "MDY").date;
        sDate = resolveWithPartner(sv, ev) || parseUploadDateStrict(sv, "MDY").date;
      } else if (startSlash && parseInt(startSlash[2], 10) > 12) {
        sDate = parseUploadDateStrict(sv, "MDY").date;
        eDate = resolveWithPartner(ev, sv) || parseUploadDateStrict(ev, "MDY").date;
      } else {
        sDate = resolveWithPartner(sv, ev) || (fileFormatHint ? parseUploadDateStrict(sv, fileFormatHint).date : null) || parseUploadDateStrict(sv, null).date;
        eDate = resolveWithPartner(ev, sv) || (fileFormatHint ? parseUploadDateStrict(ev, fileFormatHint).date : null) || parseUploadDateStrict(ev, null).date;
      }
      if (!sDate) sDate = parseUploadDateStrict(sv, null).date;
      if (!eDate) eDate = parseUploadDateStrict(ev, null).date;
      if (sDate) r[startKey] = toIsoDateString(sDate);
      if (eDate) r[endKey] = toIsoDateString(eDate);
    });
  }

  return convertedData;
};

export const normalizeAllDateColumnsInRows = (convertedData, options = {}) => {
  if (!convertedData || convertedData.length === 0) return convertedData;

  normalizeStartEndPairsInRows(convertedData);

  const dateKeys = Object.keys(convertedData[0] || {}).filter((k) => k !== "__rowNumber" && k.toLowerCase().includes("date"));
  const startKey = dateKeys.find((k) => {
    const kl = k.toLowerCase();
    return (kl.includes("start") || /(?:^|[\s_-])from/.test(kl)) && kl.includes("date");
  });
  const endKey = dateKeys.find((k) => {
    const kl = k.toLowerCase();
    return (kl.includes("end") || /(?:^|[\s_-])to(?:[\s_-]|date|$)/.test(kl)) && kl.includes("date");
  });
  const excludeKeys = startKey && endKey ? [startKey, endKey] : [];

  normalizeSingleDateColumnsInRows(convertedData, { ...options, excludeKeys });

  return convertedData;
};
